import React from 'react'
import { Box, Skeleton, useTheme } from '@mui/material'
import LogoMark from '../../Images/Branding/ClassicalConversations_C_White.png'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Drawer from '@mui/material/Drawer'
import Divider from '@mui/material/Divider'

const drawerWidth = 240

export const AuthSkeleton: React.FC = () => {
  const theme = useTheme()

  const mainMenuSteps = [65, 50, 55, 60, 70, 45, 40, 60, 50, 55]
  const adminMenuSteps = [65, 50, 55]

  return (
    <Box
      aria-label="auth-skeleton"
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          zIndex: 1020,
          boxShadow: 'none',
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
        }}
      >
        <Toolbar
          sx={{
            height: theme.spacing(8),
            background: theme.palette.primary.main,
            padding: 0,
          }}
        >
          <Skeleton
            variant="text"
            width={100}
            height={28}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.25)',
              borderRadius: 0.5,
              ml: 4,
            }}
          />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.customBackground?.sidebar,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            height: theme.spacing(8),
            background: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            pl: 3,
            boxSizing: 'border-box',
          }}
        >
          <img
            alt="Classical Conversations"
            src={LogoMark}
            style={{ height: '30px' }}
          />
        </Box>

        {/* UserAvatar and ActorSelector */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            mt: 1.5,
          }}
        >
          <Skeleton variant="circular" width={40} height={40} sx={{ mt: 3 }} />
          <Skeleton
            variant="text"
            width="75%"
            height={24}
            sx={{ fontWeight: 'bold' }}
          />
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          <Skeleton
            variant="rounded"
            width="100%"
            height={32}
            sx={{ borderRadius: 1 }}
          />
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {mainMenuSteps.map((widthPercent, i) => (
            <Box
              key={`main-${i}`}
              sx={{ display: 'flex', alignItems: 'center', pl: 2, py: 1.5 }}
            >
              <Box
                sx={{
                  minWidth: 56,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Skeleton
                  variant="rounded"
                  width={20}
                  height={20}
                  sx={{ borderRadius: 0.5 }}
                />
              </Box>
              <Skeleton variant="text" width={`${widthPercent}%`} height={18} />
            </Box>
          ))}

          <Divider sx={{ my: 1, mx: 2 }} />

          <Box sx={{ pl: 2, py: 1 }}>
            <Skeleton variant="text" width="30%" height={16} />
          </Box>

          {adminMenuSteps.map((widthPercent, i) => (
            <Box
              key={`admin-${i}`}
              sx={{ display: 'flex', alignItems: 'center', pl: 2, py: 1.5 }}
            >
              <Box
                sx={{
                  minWidth: 56,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Skeleton
                  variant="rounded"
                  width={20}
                  height={20}
                  sx={{ borderRadius: 0.5 }}
                />
              </Box>
              <Skeleton variant="text" width={`${widthPercent}%`} height={18} />
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            mt: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              pl: 2,
              minHeight: 48,
              bgcolor: 'action.selected',
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                minWidth: 56,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Skeleton
                variant="rounded"
                width={20}
                height={20}
                sx={{ borderRadius: 0.5 }}
              />
            </Box>
            <Skeleton variant="text" width="50%" height={18} />
          </Box>

          <Box
            sx={{ display: 'flex', alignItems: 'center', pl: 2, minHeight: 48 }}
          >
            <Box
              sx={{
                minWidth: 56,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Skeleton
                variant="rounded"
                width={20}
                height={20}
                sx={{ borderRadius: 0.5 }}
              />
            </Box>
            <Skeleton variant="text" width="40%" height={18} />
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '0.8fr 1fr',
              gap: 0.5,
              rowGap: 0,
            }}
          >
            <Skeleton variant="text" width="80%" height={12} />
            <Skeleton variant="text" width="80%" height={12} />
            <Skeleton variant="text" width="80%" height={12} />
            <Skeleton variant="text" width="80%" height={12} />
          </Box>
        </Box>
      </Drawer>
    </Box>
  )
}
